import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, RateReview, HourglassEmpty, CheckCircle, ChatBubbleOutline, FiberManualRecord } from "@mui/icons-material";
import { useGetReviewerStatsQuery, useEditQuestionMutation } from "../../../../../app/questionApiSlice";
import toast from "../../../../../helpers/toast";
import { useGetSchoolQuestionsQuery } from "../../../../../app/questionApiSlice";
import { useGetSubjectsPaginatedQuery } from "../../../subjects/subjectsApiSlice";
import { useGetClassesPaginatedQuery } from "../../../classroom/classApiSlice";
import { selectCurrentUser } from "../../../auth/authSlice";
import QuestionListCard from "../../../../customComponents/QuestionListCard";

const ReviewList = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("date");
  const [filterSubject, setFilterSubject] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const itemsPerPage = 10;
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isApproving, setIsApproving] = useState(false);

  const { data: subjectsData } = useGetSubjectsPaginatedQuery({ page: 1, per_page: 100 });
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 100 });
  const { data: stats } = useGetReviewerStatsQuery();
  const [editQuestion] = useEditQuestionMutation();

  const statCards = [
    {
      label: "Questions Reviewed",
      count: stats?.reviewed_count ?? 0,
      icon: <RateReview sx={{ fontSize: 28, color: "text.secondary" }} />,
    },
    {
      label: "Pending Review",
      count: stats?.pending_count ?? 0,
      icon: <HourglassEmpty sx={{ fontSize: 28, color: "text.secondary" }} />,
    },
    {
      label: "Approved Questions",
      count: stats?.approved_count ?? 0,
      icon: <CheckCircle sx={{ fontSize: 28, color: "text.secondary" }} />,
    },
  ];

  const { data, isLoading, isFetching } = useGetSchoolQuestionsQuery({
    page,
    per_page: itemsPerPage,
    sort_by: sortBy,
    subject: filterSubject !== "all" ? filterSubject : null,
    level: filterLevel !== "all" ? filterLevel : null,
  });

  const questions = data?.questions || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const totalQuestions = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuestions);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleFilterSubjectChange = (event) => {
    setFilterSubject(event.target.value);
    setPage(1);
  };

  const handleFilterLevelChange = (event) => {
    setFilterLevel(event.target.value);
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allOnPageSelected =
    questions.length > 0 && questions.every((q) => selectedIds.has(q.id));
  const someOnPageSelected = questions.some((q) => selectedIds.has(q.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        questions.forEach((q) => next.delete(q.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        questions.forEach((q) => next.add(q.id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkApprove = async () => {
    setIsApproving(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          editQuestion({
            values: { is_approved: true, approver_id: currentUser?.id },
            questionId: id,
          }).unwrap()
        )
      );
      setSelectedIds(new Set());
      toast.success("Selected questions approved");
    } catch (err) {
      console.error("Error bulk approving questions:", err);
      const msg = err?.data?.message || err?.message || "Failed to approve selected questions.";
      toast.error(msg);
    } finally {
      setIsApproving(false);
    }
  };

  const uniqueSubjects = React.useMemo(() => {
    if (!subjectsData?.subjects) return [];
    return [...new Set(subjectsData.subjects.map((s) => s.name).filter(Boolean))].sort();
  }, [subjectsData]);

  const uniqueLevels = React.useMemo(() => {
    if (!classroomsData?.classrooms) return [];
    const levels = classroomsData.classrooms.map((c) => c.level);
    return [...new Set(levels)].filter(Boolean).sort();
  }, [classroomsData]);

  return (
    <Box>
      {/* Reviewer Stats Cards */}
      <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "auto",
              sm: "auto",
              md: "auto auto auto",
              lg: "auto auto auto",
            },
            gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
            mb: 3,
          }}
        >
          {statCards.map((card) => (
            <Box
              key={card.label}
              sx={{
                borderRadius: "6px",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                padding: "2em 1.5em",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
                minWidth: "18.2em",
              }}
            >
              <Box
                sx={{
                  padding: ".5em .7em",
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F4F6FAB2",
                  borderRadius: "10px",
                  marginBottom: ".5em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {card.icon}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: ".9rem", md: "1rem" }, fontWeight: 500 }}
              >
                {card.label} ({card.count})
              </Typography>
            </Box>
          ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={allOnPageSelected ? "Deselect all" : "Select all on page"}>
            <Checkbox
              checked={allOnPageSelected}
              indeterminate={someOnPageSelected && !allOnPageSelected}
              onChange={toggleSelectAll}
              size="small"
              sx={{ p: 0.5 }}
            />
          </Tooltip>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Questions
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Select
            value={filterSubject}
            onChange={handleFilterSubjectChange}
            size="small"
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Subjects</MenuItem>
            {uniqueSubjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {subject}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={filterLevel}
            onChange={handleFilterLevelChange}
            size="small"
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Levels</MenuItem>
            {uniqueLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={sortBy}
            onChange={handleChangeSort}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="date">Sort by: Date</MenuItem>
            <MenuItem value="subject">Sort by: Subject</MenuItem>
            <MenuItem value="level">Sort by: Level</MenuItem>
          </Select>
        </Box>
      </Box>

      {(isLoading || isFetching) && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {questions.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            No questions found
          </Typography>
        </Box>
      )}

      {questions.map((question) => {
        const reviewCount = question.reviews?.length || 0;
        const activeReviews = question.reviews?.filter((r) => !r.is_deleted) || [];
        const latestReview = activeReviews.length
          ? activeReviews.reduce((a, b) =>
              new Date(a.created_at) > new Date(b.created_at) ? a : b
            )
          : null;
        const needsResponse = latestReview && latestReview.user_id !== currentUser?.id;

        return (
        <Box key={question.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Checkbox
            checked={selectedIds.has(question.id)}
            onChange={() => toggleSelect(question.id)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ mt: 1.5, p: 0.5, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <QuestionListCard
              question={question}
              onClick={() =>
                navigate(`/lms/reviews/${question.id}`, {
                  state: {
                    listContext: {
                      source: "school-reviews",
                      page,
                      perPage: itemsPerPage,
                      sortBy,
                      filterSubject,
                      filterLevel,
                    },
                  },
                })
              }
              formatDate={formatDate}
              topBadge={(reviewCount > 0 || needsResponse) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {reviewCount > 0 && (
                    <Chip
                      icon={<ChatBubbleOutline sx={{ fontSize: 14 }} />}
                      label={`${reviewCount} review${reviewCount !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ fontSize: "0.75rem", height: 24, backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a2a3a" : "#E8F0FE", color: "primary.safeOnLight", "& .MuiChip-icon": { color: "primary.safeOnLight" } }}
                    />
                  )}
                  {needsResponse && (
                    <Chip
                      icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
                      label="Needs your response"
                      size="small"
                      sx={{ fontSize: "0.75rem", height: 24, backgroundColor: (theme) => theme.palette.mode === "dark" ? "#3a2000" : "#FFF3E0", color: "#E65100", "& .MuiChip-icon": { color: "#E65100" } }}
                    />
                  )}
                </Box>
              )}
            />
          </Box>
        </Box>
        );
      })}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2em",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {totalQuestions > 0 ? startIndex : 0} to {endIndex} of {totalQuestions} entries
        </Typography>

        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          color="primary"
          shape="rounded"
          sx={{ color: "#CCCCCC" }}
        />
      </Box>

      {/* Floating bulk approve bar */}
      {selectedIds.size > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: (theme) => theme.palette.mode === "dark" ? "#1e1e2e" : "#fff",
            boxShadow: 6,
            borderRadius: 2,
            px: 3,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            zIndex: 1200,
            border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e0e0e0"}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedIds.size} question{selectedIds.size !== 1 ? "s" : ""} selected
          </Typography>
          <Button variant="outlined" size="small" onClick={clearSelection} disabled={isApproving}>
            Clear
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleOutline />}
            onClick={handleBulkApprove}
            disabled={isApproving}
          >
            {isApproving ? "Approving..." : "Approve Selected"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReviewList;
